import { jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useProjectStore } from '../store/project-store';
import { useTasks, useSprints } from '../hooks/useProject';
function generateBurndown(sprintDays) {
    const ideal = Array.from({ length: sprintDays + 1 }, (_, i) => 100 - (100 / sprintDays) * i);
    const actual = ideal.map((v, i) => (i === 0 ? 100 : Math.max(0, v + (Math.random() * 20 - 10))));
    return `xychart-beta
    title "Burndown Chart"
    x-axis ["Day ${Array.from({ length: sprintDays + 1 }, (_, i) => i + 1).join('", "Day ')}"]
    y-axis "Story Points" 0 --> 100
    line "Ideal" [${ideal.join(', ')}]
    line "Actual" [${actual.map((v) => Math.round(v)).join(', ')}]`;
}
function generateBurnup(tasks) {
    const total = tasks.reduce((s, t) => s + (t.storyPoints || 1), 0);
    const done = tasks.filter((t) => t.status === 'done').reduce((s, t) => s + (t.storyPoints || 1), 0);
    return `xychart-beta
    title "Burnup Chart"
    x-axis ["Start", "Current"]
    y-axis "Story Points" 0 --> ${Math.max(total + 10, 20)}
    line "Total Scope" [${total}, ${total}]
    line "Completed" [0, ${done}]`;
}
function generateVelocityChart(sprints) {
    if (sprints.length === 0)
        return 'xychart-beta\n    title "Velocity"\n    x-axis ["No sprints"]\n    y-axis "Points" 0 --> 10\n    bar [0]';
    return `xychart-beta
    title "Velocity Chart"
    x-axis [${sprints.map((s) => `"${s.name}"`).join(', ')}]
    y-axis "Story Points" 0 --> ${Math.max(...sprints.map((s) => s.velocity), 10) + 5}
    bar [${sprints.map((s) => s.velocity).join(', ')}]`;
}
function generateCycleTime() {
    return `xychart-beta
    title "Cycle Time (days)"
    x-axis ["Week 1", "Week 2", "Week 3", "Week 4"]
    y-axis "Days" 0 --> 10
    line [3.2, 2.8, 4.1, 2.5]`;
}
function generateLeadTime() {
    return `xychart-beta
    title "Lead Time (days)"
    x-axis ["Week 1", "Week 2", "Week 3", "Week 4"]
    y-axis "Days" 0 --> 20
    line [8.5, 7.2, 10.1, 6.8]`;
}
function generateThroughput(tasks) {
    const weekly = [0, 0, 0, 0];
    const now = Date.now();
    tasks.forEach((t) => {
        const age = (now - new Date(t.created).getTime()) / (7 * 24 * 60 * 60 * 1000);
        if (age < 1)
            weekly[0]++;
        else if (age < 2)
            weekly[1]++;
        else if (age < 3)
            weekly[2]++;
        else if (age < 4)
            weekly[3]++;
    });
    return `xychart-beta
    title "Throughput (tasks/week)"
    x-axis ["This Week", "2 Weeks Ago", "3 Weeks Ago", "4 Weeks Ago"]
    y-axis "Tasks" 0 --> ${Math.max(...weekly, 5) + 2}
    bar [${weekly.join(', ')}]`;
}
function generateCumulativeFlow() {
    const days = 14;
    const backlog = Array.from({ length: days }, (_, i) => 20 - Math.floor(i / 3));
    const todo = Array.from({ length: days }, (_, i) => 5 + Math.floor(i / 2));
    const inProgress = Array.from({ length: days }, (_, i) => 8 + Math.floor(i / 4));
    const done = Array.from({ length: days }, (_, i) => Math.floor(i * 1.2));
    return `xychart-beta
    title "Cumulative Flow Diagram"
    x-axis ${JSON.stringify(Array.from({ length: days }, (_, i) => `Day ${i + 1}`))}
    y-axis "Tasks" 0 --> 30
    line "Backlog" [${backlog.join(', ')}]
    line "To Do" [${todo.join(', ')}]
    line "In Progress" [${inProgress.join(', ')}]
    line "Done" [${done.join(', ')}]`;
}
function generateGantt() {
    return `gantt
    title Project Timeline
    dateFormat YYYY-MM-DD
    section Design
    Homepage Design :a1, 2026-06-01, 5d
    Style Guide :a2, after a1, 3d
    section Development
    Responsive Nav :b1, 2026-06-08, 4d
    CI/CD Pipeline :b2, 2026-06-10, 3d
    section Polish
    Image Optimization :c1, after b1, 2d`;
}
function getChartLabels(t) {
    return {
        burndown: t('reports.burndown'),
        burnup: t('reports.burnup'),
        velocity: t('reports.velocity'),
        'cycle-time': t('reports.cycleTime'),
        'lead-time': t('reports.leadTime'),
        throughput: t('reports.throughput'),
        'cumulative-flow': t('reports.cumulativeFlow'),
        gantt: t('reports.gantt'),
    };
}
function renderMermaid(svg) {
    return svg
        .replace(/<svg[^>]*>/, (match) => match.replace('class="', 'class="mermaid-svg '))
        .replace(/<style>[^<]*<\/style>/, '');
}
export function ChartsView() {
    const { t } = useTranslation();
    const { projectId } = useParams();
    const { tasks } = useTasks(projectId);
    const { sprints } = useSprints(projectId);
    const projects = useProjectStore((s) => s.projects);
    const project = projects.find((p) => p.project_id === projectId);
    const [chartType, setChartType] = useState('burndown');
    const [selectedSprint, setSelectedSprint] = useState('');
    const [mermaidSvg, setMermaidSvg] = useState('');
    const mermaidRef = useRef(null);
    const chartLabels = useMemo(() => getChartLabels(t), [t]);
    const mermaidSyntax = useMemo(() => {
        switch (chartType) {
            case 'burndown':
                return generateBurndown(14);
            case 'burnup':
                return generateBurnup(tasks);
            case 'velocity':
                return generateVelocityChart(sprints);
            case 'cycle-time':
                return generateCycleTime();
            case 'lead-time':
                return generateLeadTime();
            case 'throughput':
                return generateThroughput(tasks);
            case 'cumulative-flow':
                return generateCumulativeFlow();
            case 'gantt':
                return generateGantt();
        }
    }, [chartType, tasks, sprints]);
    useEffect(() => {
        let cancelled = false;
        async function render() {
            try {
                const mermaid = (await import('mermaid')).default;
                mermaid.initialize({
                    startOnLoad: false,
                    theme: 'dark',
                    themeVariables: {
                        primaryColor: '#6366f1',
                        primaryTextColor: '#e2e8f0',
                        lineColor: '#94a3b8',
                    },
                });
                const id = `chart-${Date.now()}`;
                const { svg } = await mermaid.render(id, mermaidSyntax);
                if (!cancelled)
                    setMermaidSvg(renderMermaid(svg));
            }
            catch {
                if (!cancelled)
                    setMermaidSvg(`<pre class="text-sm text-text-muted">${mermaidSyntax}</pre>`);
            }
        }
        render();
        return () => { cancelled = true; };
    }, [mermaidSyntax]);
    const summary = useMemo(() => {
        switch (chartType) {
            case 'burndown':
                return `Shows ideal vs actual progress over ${14} days. Ideal line assumes even velocity.`;
            case 'burnup':
                return `Tracks completed work against total scope. Scope changes would move the total line.`;
            case 'velocity':
                return `Sprint velocity over time. Average: ${sprints.length > 0 ? Math.round(sprints.reduce((s, sp) => s + sp.velocity, 0) / sprints.length) : 0} pts/sprint.`;
            case 'cycle-time':
                return `Average time from start to completion. Lower is better.`;
            case 'lead-time':
                return `Average time from request to delivery. Lower indicates faster throughput.`;
            case 'throughput':
                return `Number of tasks completed per week. Consistent throughput indicates stable capacity.`;
            case 'cumulative-flow':
                return `Shows work distribution across states over time. Narrowing bands indicate less WIP.`;
            case 'gantt':
                return `Project timeline with task dependencies and estimated durations.`;
        }
    }, [chartType, sprints]);
    const dataSummary = useMemo(() => {
        const total = tasks.length;
        const done = tasks.filter((t) => t.status === 'done').length;
        const points = tasks.reduce((s, t) => s + (t.storyPoints || 0), 0);
        return { total, done, points };
    }, [tasks]);
    return (_jsxs("div", { className: "space-y-6", children: [_jsx("div", { className: "flex items-center justify-between", children: _jsxs("h2", { className: "text-2xl font-bold text-text-primary", children: [t('nav.charts'), " \u2014 ", project?.name || projectId] }) }), _jsxs("div", { className: "flex flex-wrap gap-3 items-center", children: [_jsx("div", { className: "flex gap-1 bg-bg-secondary rounded-lg p-1 flex-wrap", children: Object.keys(chartLabels).map((type) => (_jsx("button", { onClick: () => setChartType(type), className: `px-3 py-1.5 text-sm rounded-md transition-colors ${chartType === type
                                ? 'bg-primary text-white'
                                : 'text-text-secondary hover:bg-bg-primary'}`, children: chartLabels[type] }, type))) }), sprints.length > 0 && (chartType === 'burndown' || chartType === 'burnup') && (_jsxs("select", { value: selectedSprint, onChange: (e) => setSelectedSprint(e.target.value), className: "input", children: [_jsxs("option", { value: "", children: [t('sprint.title'), " ", t('common.all')] }), sprints.map((s) => (_jsx("option", { value: s.id, children: s.name }, s.id)))] }))] }), _jsxs("div", { className: "grid grid-cols-1 lg:grid-cols-4 gap-4", children: [_jsxs("div", { className: "card p-4", children: [_jsx("p", { className: "text-xs text-text-muted uppercase tracking-wide", children: t('dashboard.totalTasks') }), _jsx("p", { className: "text-2xl font-bold text-text-primary mt-1", children: dataSummary.total })] }), _jsxs("div", { className: "card p-4", children: [_jsx("p", { className: "text-xs text-text-muted uppercase tracking-wide", children: t('common.done') }), _jsx("p", { className: "text-2xl font-bold text-text-primary mt-1", children: dataSummary.done })] }), _jsxs("div", { className: "card p-4", children: [_jsx("p", { className: "text-xs text-text-muted uppercase tracking-wide", children: t('task.storyPoints') }), _jsx("p", { className: "text-2xl font-bold text-text-primary mt-1", children: dataSummary.points })] }), _jsxs("div", { className: "card p-4", children: [_jsx("p", { className: "text-xs text-text-muted uppercase tracking-wide", children: t('dashboard.completion') }), _jsxs("p", { className: "text-2xl font-bold text-text-primary mt-1", children: [dataSummary.total > 0 ? Math.round((dataSummary.done / dataSummary.total) * 100) : 0, "%"] })] })] }), _jsxs("div", { className: "card p-6", children: [_jsxs("h3", { className: "text-lg font-semibold text-text-primary mb-2", children: [chartLabels[chartType], " ", t('nav.charts')] }), _jsx("p", { className: "text-sm text-text-secondary mb-4", children: summary }), _jsx("div", { ref: mermaidRef, className: "flex justify-center overflow-auto", dangerouslySetInnerHTML: { __html: mermaidSvg || `<p class="text-text-muted">${t('common.loading')}</p>` } })] }), _jsxs("div", { className: "card p-4", children: [_jsx("h3", { className: "text-sm font-semibold text-text-primary mb-2", children: "Mermaid Source" }), _jsx("pre", { className: "text-xs text-text-muted bg-bg-secondary p-3 rounded overflow-auto max-h-48", children: mermaidSyntax })] })] }));
}
//# sourceMappingURL=ChartsView.js.map